import { createSlice } from '@reduxjs/toolkit';
import client from 'api/apiClient';
import { createAsyncThunkWrapper } from 'redux/thunk/createAsyncThunkWrapper';
import { getDefaultCompanyId } from 'utils/helpers';

export enum QR_STATUS {
    ALL = 'ALL',
    ACTIVE = 'ACTIVE',
    INACTIVE = 'INACTIVE',
    DELETED = 'DELETED',
    PENDING = 'PENDING',
}

export interface MerchantQRCode {
    companyCode: string;
    createdDate: string;
    description: string;
    entityId: number;
    id: string;
    label: string;
    status: QR_STATUS;
    updatedDate: string;
    uuid: string;
}
export interface MerchantQRString {
    qrId: string;
    string: string;
}

export interface AddQRPayload {
    companyId: number;
    description: string;
    id: number;
    label: string;
    status: QR_STATUS;
    valid: boolean;
    validUpdate: boolean;
}

interface CreateQRCodePayload {
    label: string;
    description: string;
}

interface QRCodeState {
    allQrCodes: MerchantQRCode[];
    qrStrings: MerchantQRString[];
    loading: boolean;
    error: boolean;
}
const initialState: QRCodeState = {
    allQrCodes: [],
    qrStrings: [],
    loading: false,
    error: false,
};

export const sampleResponse: any = {
    statusCode: 'OK',
    code: 200,
    msg: 'Success',
    contextResponse: {
        tenantName: 'RCC',
        statusCode: 'SUCCESS',
        errors: {},
    },
    companyQRCodes: [
        {
            id: '202',
            uuid: '96214dbc-afde-449c-9c53-47af55c3bc81',
            entityId: 15628,
            companyCode: 'C0370',
            label: 'Ahapay',
            description: 'default',
            status: 'ACTIVE',
            createdDate: '2023-12-19T15:26:41.552+00:00',
            updatedDate: '2023-12-19T15:26:41.552+00:00',
        },
    ],
    errors: {},
};

export const getQRCodes = createAsyncThunkWrapper<any, QR_STATUS>('qrCode/getQRCodes', async (status: QR_STATUS, { getState }) => {
    const state: any = getState();
    const companyId = getDefaultCompanyId(state);
    if (!companyId) return;

    // FIX ME

    const res: any = await client.get('/merchant/qrCode', {
        headers: {
            companyId,
            status,
        },
    });
    const companyQRCodes = sampleResponse.companyQRCodes;
    const prevStrings = state.qrCode?.qrStrings || [];
    const qrStrings = await generateQRStrings(companyQRCodes, prevStrings);
    return { companyQRCodes, qrStrings };
});

const generateQRStrings = async (qrCodes: MerchantQRCode[], prevStrings: MerchantQRString[]) => {
    if (!Array.isArray(qrCodes) || !qrCodes?.length) return [];
    const qrStrings: MerchantQRString[] = [];
    let qrString = '';
    await Promise.all(
        qrCodes.map(async (qr) => {
            const qrStringObject = prevStrings?.find((qrString) => qrString.qrId === qr.id);
            if (!qrStringObject) {
                qrString = await encodeQRString(JSON.stringify({ ...qr, isMerchant: true }));
            } else {
                qrString = qrStringObject.string;
            }
            qrStrings.push({ qrId: qr.id, string: qrString });
        })
    );
    return qrStrings;
};

const encodeQRString = async (str: string): Promise<string> => {
    const res: any = await client.post('encodeString', { stringRequest: str });
    return res;
};

export const downloadQR = (id: string, filename: string) => {
    const canvas = document.getElementById(id) as HTMLCanvasElement;
    const pngUrl = canvas.toDataURL('image/png').replace('image/png', 'image/octet-stream');
    const downloadLink = document.createElement('a');
    downloadLink.href = pngUrl;
    downloadLink.download = `${filename}.png`;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
};

export const getQRCodeById = (strings: MerchantQRString[], id: string) => {
    if (!Array.isArray(strings) || !id) return '';
    return strings.find((str) => str.qrId === id)?.string || '';
};

export const createQRCode = createAsyncThunkWrapper<any, CreateQRCodePayload>(
    'qrCode/createQRCode',
    async ({ label, description }: CreateQRCodePayload, { getState }) => {
        const state: any = getState();
        const companyId = getDefaultCompanyId(state);
        if (!companyId) return;

        const payload = {
            companyId,
            label,
            description,
            id: 0,
            status: QR_STATUS.ACTIVE,
            valid: true,
            validUpdate: true,
        };

        const res: any = await client.post('merchant/qrCode', payload);
        return res;
    }
);

const qrCodeSlice = createSlice({
    name: 'qrCode',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(getQRCodes.pending, (state) => {
            state.loading = true;
            state.error = false;
        });
        builder.addCase(getQRCodes.fulfilled, (state, action) => {
            state.loading = false;
            state.error = false;
            const { payload } = action || {};
            if (!payload) return;
            state.allQrCodes = payload.companyQRCodes;
            state.qrStrings = payload.qrStrings;
        });
        builder.addCase(getQRCodes.rejected, (state) => {
            state.loading = false;
            state.error = true;
        });
        builder.addCase(createQRCode.pending, (state) => {
            state.loading = true;
            state.error = false;
        });
        builder.addCase(createQRCode.fulfilled, (state) => {
            state.loading = false;
            // NO NEED TO UPDATE STATE AS WE WILL EVENTUALLY FETCH THE GENERATED QR CODE WITH GETQRCODE API
            // ADDED THE BUILDER CASE TO SHOW THE LOADING UNTIL QR CODES ARE RETURNED FROM BACKEND
        });
        builder.addCase(createQRCode.rejected, (state) => {
            state.loading = false;
            state.error = true;
        });
    },
});

export default qrCodeSlice.reducer;
